/**
 * Admin Action Centre — surfaces stale contracts + unpaid invoices at-a-glance
 * on /admin so the admin sees "what's waiting" before drilling into sub-pages.
 *
 * Reuses the same /admin/contracts/stats + /admin/invoices/stats endpoints
 * the per-vault dashboards use, and triggers a soft-link to those pages
 * pre-filtered to the stale slice.
 *
 * Empty state ("All clear") renders when stale_awaiting + stale_unpaid are
 * both 0 — admins love a green check first thing in the morning.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, FileText, Receipt, Clock, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function AdminActionCentre() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let live = true;
    Promise.all([
      api.get("/admin/contracts/stats").then((r) => r.data).catch(() => null),
      api.get("/admin/invoices/stats").then((r) => r.data).catch(() => null),
    ]).then(([contracts, invoices]) => {
      if (!live) return;
      if (!contracts && !invoices) { setErr(true); return; }
      setStats({ contracts: contracts || {}, invoices: invoices || {} });
    });
    return () => { live = false; };
  }, []);

  if (err) return null;
  if (!stats) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 flex items-center gap-2.5 text-slate-500 text-[12.5px]" data-testid="admin-action-centre-loading">
        <Loader2 className="size-3.5 animate-spin" /> Loading action centre…
      </div>
    );
  }

  const staleContracts = stats.contracts.stale_awaiting || 0;
  const staleInvoices  = stats.invoices.stale_unpaid || 0;
  const outstandingGbp = Number(stats.invoices.outstanding_gbp || 0);

  // ---- Empty state: nothing waiting ----
  if (staleContracts === 0 && staleInvoices === 0) {
    return (
      <div className="rounded-2xl border border-emerald-200/70 bg-gradient-to-r from-emerald-50/60 via-white to-white px-5 py-4 flex items-center gap-3" data-testid="admin-action-centre-clear">
        <span className="inline-grid place-items-center size-9 rounded-lg bg-emerald-500/15 text-emerald-600 flex-shrink-0">
          <CheckCircle2 className="size-4" strokeWidth={2.2} />
        </span>
        <div>
          <div className="text-[13.5px] font-semibold text-slate-900 tracking-tight">All clear · no contracts or invoices need chasing.</div>
          <div className="text-[12px] text-slate-500">Action centre is empty. Stale contracts (&gt;7d) and unpaid invoices (&gt;14d) appear here when they need attention.</div>
        </div>
      </div>
    );
  }

  // ---- Cards (1 or 2 depending on what's stale) ----
  return (
    <div className="space-y-3" data-testid="admin-action-centre">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#0055FF]">Action centre</div>
          <h2 className="text-base font-semibold text-slate-900 tracking-tight mt-0.5">
            {staleContracts + staleInvoices} item{(staleContracts + staleInvoices) === 1 ? "" : "s"} waiting on you
          </h2>
        </div>
      </div>

      <div className={`grid gap-3 ${staleContracts > 0 && staleInvoices > 0 ? "md:grid-cols-2" : "grid-cols-1"}`}>
        {staleContracts > 0 && (
          <Link
            to="/admin/contracts"
            className="group rounded-2xl border border-slate-200 bg-white px-5 py-4 hover:border-[#0055FF]/40 hover:-translate-y-0.5 transition-all flex items-start gap-3"
            data-testid="action-centre-contracts"
          >
            <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0055FF]/10 text-[#0055FF] flex-shrink-0">
              <FileText className="size-4" strokeWidth={2} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Clock className="size-3 text-slate-400" />
                <span className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-slate-500">Pending signature &gt; 7 days</span>
              </div>
              <div className="mt-1 text-[20px] font-semibold text-slate-900 tracking-tight tabular-nums">
                {staleContracts} contract{staleContracts === 1 ? "" : "s"}
              </div>
              <p className="mt-0.5 text-[12px] text-slate-500">
                Open the vault to chase, resend, or trigger the bulk Nudge-all flow.
              </p>
            </div>
            <ArrowRight className="size-4 text-slate-300 group-hover:text-[#0055FF] group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1" />
          </Link>
        )}

        {staleInvoices > 0 && (
          <Link
            to="/admin/invoices"
            className="group rounded-2xl border border-slate-200 bg-white px-5 py-4 hover:border-[#0055FF]/40 hover:-translate-y-0.5 transition-all flex items-start gap-3"
            data-testid="action-centre-invoices"
          >
            <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0055FF]/10 text-[#0055FF] flex-shrink-0">
              <Receipt className="size-4" strokeWidth={2} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Clock className="size-3 text-slate-400" />
                <span className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-slate-500">Unpaid &gt; 14 days</span>
              </div>
              <div className="mt-1 flex items-baseline gap-2 tracking-tight">
                <span className="text-[20px] font-semibold text-slate-900 tabular-nums">
                  {staleInvoices} invoice{staleInvoices === 1 ? "" : "s"}
                </span>
                <span className="text-[13px] text-[#0055FF] font-semibold tabular-nums">
                  · £{outstandingGbp.toLocaleString()} outstanding
                </span>
              </div>
              <p className="mt-0.5 text-[12px] text-slate-500">
                Open the invoices vault to chase, resend PDFs, or trigger the bulk Nudge-all (with dry-run preview).
              </p>
            </div>
            <ArrowRight className="size-4 text-slate-300 group-hover:text-[#0055FF] group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1" />
          </Link>
        )}
      </div>
    </div>
  );
}
